import React, { useState, useEffect } from "react";
import { getSyncLogs, getSyncLog } from "../services/api";
import ProgressBar from "../components/ProgressBar";

/**
 * Liefert die Zusammenfassungs-Kacheln passend zum jeweiligen sync_type,
 * da die summary-JSON-Struktur je nach Sync-Vorgang unterschiedliche Felder
 * enthält (z.B. AD/WP-Felder beim HR-Import vs. created/updated/deleted bei
 * der Kontaktverteilung).
 */
function summaryCards(syncType, summary) {
  const errCount = summary.errors?.length || 0;

  switch (syncType) {
    case "hrlab_import":
      return [
        ["Importiert", summary.imported ?? 0, "#0078d4"],
        ["Übersprungen", summary.skipped ?? 0, "#999999"],
        ["Fehler", errCount, "#d13438"],
      ];
    case "full":
    case "manual":
      return [
        ["AD angelegt", summary.created_ad ?? 0, "#0078d4"],
        ["AD deaktiviert", summary.deactivated_ad ?? 0, "#ff8c00"],
        ["Übersprungen", summary.skipped ?? 0, "#999999"],
        ["Fehler", errCount, "#d13438"],
      ];
    case "contacts":
      return [
        ["Postfächer verarbeitet", summary.owners_processed ?? 0, "#0078d4"],
        ["Angelegt", summary.created ?? 0, "#107c10"],
        ["Aktualisiert", summary.updated ?? 0, "#ff8c00"],
        ["Entfernt", summary.deleted ?? 0, "#5c2d91"],
        ["Fehler", errCount, "#d13438"],
      ];
    case "contacts_owner":
    case "contacts_single":
      return [
        ["Angelegt", summary.created ?? 0, "#107c10"],
        ["Aktualisiert", summary.updated ?? 0, "#ff8c00"],
        ["Entfernt", summary.deleted ?? 0, "#5c2d91"],
        ["Fehler", errCount, "#d13438"],
      ];
    case "ad_profile_sync":
      return [
        ["Verarbeitet", summary.processed ?? 0, "#0078d4"],
        ["Aktualisiert", summary.updated ?? 0, "#107c10"],
        ["Übersprungen", summary.skipped ?? 0, "#999999"],
        ["Fehler", errCount, "#d13438"],
      ];
    case "contacts_dedupe":
      return [
        ["Postfächer bereinigt", summary.owners_processed ?? 0, "#0078d4"],
        ["Duplikate entfernt", summary.removed ?? 0, "#107c10"],
        ["Fehler", errCount, "#d13438"],
      ];
    case "wp_office":
      // WP-Office-Sync (Plugin-Fernsteuerung): Delta vs. Bulk anhand summary.mode.
      if (summary.mode === "bulk") {
        return [
          ["Modus", "Bulk", "#0078d4"],
          ["Status", summary.status ?? "—", "#5c2d91"],
          ["Verarbeitet", summary.processed ?? 0, "#107c10"],
          ["Fehler", errCount, "#d13438"],
        ];
      }
      return [
        ["Modus", "Delta", "#0078d4"],
        ["Angelegt/Akt.", summary.upserted ?? 0, "#107c10"],
        ["Deaktiviert", summary.deactivated ?? 0, "#ff8c00"],
        ["Übersprungen", summary.skipped ?? 0, "#999999"],
        ["Fehler", summary.error_count ?? errCount, "#d13438"],
      ];
    default:
      return [["Fehler", errCount, "#d13438"]];
  }
}

export default function LogsPage() {
  const [logs, setLogs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getSyncLogs(50)
      .then((res) => setLogs(res.data.logs || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const loadDetail = async (log) => {
    setSelected(log.id);
    setDetail(null);
    const res = await getSyncLog(log.id);
    setDetail(res.data);
  };

  const statusColor = { success: "#107c10", error: "#d13438", running: "#ff8c00" };
  const statusLabel = { success: "Erfolgreich", error: "Fehler", running: "Läuft" };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700 }}>Sync-Protokoll</h1>
        <p style={{ color: "#666", marginTop: 4 }}>Alle Synchronisierungs-Vorgänge</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.6fr", gap: 20, alignItems: "start" }}>
        {/* Log-Liste */}
        <div className="card" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ padding: 40 }}><ProgressBar label="Lade Logs…" /></div>
          ) : logs.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "#666" }}>Noch keine Syncs</div>
          ) : logs.map((log) => (
            <div
              key={log.id}
              onClick={() => loadDetail(log)}
              style={{
                padding: "14px 20px", borderBottom: "1px solid #f0f0f0", cursor: "pointer",
                background: selected === log.id ? "#f0f7ff" : "white",
                borderLeft: selected === log.id ? "3px solid #0078d4" : "3px solid transparent",
              }}
              onMouseOver={(e) => { if (selected !== log.id) e.currentTarget.style.background = "#f8f9fa"; }}
              onMouseOut={(e) => { if (selected !== log.id) e.currentTarget.style.background = "white"; }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span className={`badge badge-${log.status === "success" ? "success" : log.status === "error" ? "danger" : "warning"}`}>
                  {statusLabel[log.status] || log.status}
                </span>
                <span style={{ fontSize: 12, color: "#999" }}>
                  {new Date(log.started_at).toLocaleDateString("de-DE")}
                </span>
              </div>
              <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                {new Date(log.started_at).toLocaleTimeString("de-DE")} · {log.triggered_by?.split("@")[0]}
                {log.actions_success > 0 && <span style={{ color: "#107c10" }}> · {log.actions_success} Aktionen</span>}
                {log.actions_error > 0 && <span style={{ color: "#d13438" }}> · {log.actions_error} Fehler</span>}
              </div>
            </div>
          ))}
        </div>

        {/* Detail */}
        <div className="card">
          {!detail ? (
            <div style={{ textAlign: "center", color: "#999", padding: 40 }}>
              {selected ? "Lade Details..." : "← Sync auswählen"}
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <h2 style={{ fontSize: 16, fontWeight: 600 }}>Sync #{detail.log.id}</h2>
                  <span className={`badge badge-${detail.log.status === "success" ? "success" : "danger"}`}>
                    {statusLabel[detail.log.status]}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: "#666", marginTop: 4 }}>
                  {new Date(detail.log.started_at).toLocaleString("de-DE")} · von {detail.log.triggered_by}
                </div>
              </div>

              {detail.log.summary && (
                <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
                  {summaryCards(detail.log.sync_type, detail.log.summary).map(([label, val, color]) => (
                    <div key={label} style={{ padding: "8px 14px", background: `${color}15`, borderRadius: 6, fontSize: 13 }}>
                      <span style={{ fontWeight: 700, color }}>{val}</span> {label}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ maxHeight: 400, overflowY: "auto" }}>
                {detail.actions.map((a) => (
                  <div key={a.id} style={{
                    padding: "8px 12px", borderRadius: 6, marginBottom: 6,
                    background: a.status === "success" ? "#f0fff0" : a.status === "error" ? "#fff0f0" : "#fffbe6",
                    fontSize: 13
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <strong>{a.employee_name}</strong>
                      <span style={{ fontSize: 11, color: "#999" }}>{a.target}</span>
                    </div>
                    <div style={{ color: "#666", marginTop: 2 }}>
                      {a.action.replace(/_/g, " ")}
                      {a.message && <span style={{ color: "#d13438" }}> · {a.message}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
